// Conversor do SUBSET de Markdown usado em /docs/api-publica.md para um
// docDefinition do pdfmake. Funcao pura (testavel com jest, sem DOM).
// Subset: # a ####, paragrafos, listas "- ", tabelas "|", blocos ``` e
// inline **negrito** / `codigo`. Linha fora do subset vira paragrafo simples.

export function textoInline(str) {
  const partes = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let ultimo = 0;
  let m;
  while ((m = re.exec(str)) !== null) {
    if (m.index > ultimo) partes.push({ text: str.slice(ultimo, m.index) });
    const token = m[0];
    if (token.startsWith('**')) partes.push({ text: token.slice(2, -2), bold: true });
    else partes.push({ text: token.slice(1, -1), style: 'inlineCode' });
    ultimo = m.index + token.length;
  }
  if (ultimo < str.length) partes.push({ text: str.slice(ultimo) });
  if (partes.length === 0) return '';
  if (partes.length === 1 && !partes[0].bold && !partes[0].style) return partes[0].text;
  return partes;
}

export function mdParaPdfmake(md) {
  const linhas = String(md || '').split(/\r?\n/);
  const content = [];
  let bufferCodigo = null; // linhas dentro de ``` ... ```
  let bufferTabela = null; // linhas "| ... |"
  let bufferLista = null;  // itens "- " consecutivos

  // pdfmake trata array cru como stack (linhas empilhadas); para texto inline
  // misto o node precisa ser { text: [...] }. String simples passa direto.
  const noInline = (str) => {
    const t = textoInline(str);
    return Array.isArray(t) ? { text: t } : t;
  };

  const flushLista = () => {
    if (bufferLista && bufferLista.length) {
      content.push({ ul: bufferLista, margin: [8, 2, 0, 6] });
    }
    bufferLista = null;
  };

  const flushTabela = () => {
    if (!bufferTabela) return;
    // remove a linha separadora |---|---|
    const uteis = bufferTabela.filter((l) => !/^\s*\|[\s\-:|]+\|\s*$/.test(l));
    const body = uteis.map((l) =>
      l.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => noInline(c.trim()))
    );
    if (body.length) {
      const nCols = Math.max(...body.map((r) => r.length));
      body.forEach((r) => { while (r.length < nCols) r.push(''); });
      content.push({
        table: { headerRows: 1, widths: Array(nCols).fill('*'), body },
        style: 'tabela',
        margin: [0, 4, 0, 8],
      });
    }
    bufferTabela = null;
  };

  const pushCodigo = (texto) => {
    // bloco de codigo = tabela de 1 celula com fundo (pdfmake nao tem
    // background em paragrafo). Fonte embutida e so Roboto: sem mono real.
    content.push({
      table: { widths: ['*'], body: [[{
        text: texto || ' ',
        style: 'codigo',
        fillColor: '#f3f4f6',
        preserveLeadingSpaces: true,
      }]] },
      layout: 'noBorders',
      margin: [0, 4, 0, 8],
    });
  };

  for (const linha of linhas) {
    if (bufferCodigo !== null) {
      if (/^```/.test(linha)) { pushCodigo(bufferCodigo.join('\n')); bufferCodigo = null; }
      else bufferCodigo.push(linha);
      continue;
    }
    if (/^```/.test(linha)) { flushLista(); flushTabela(); bufferCodigo = []; continue; }
    if (/^\s*\|.*\|\s*$/.test(linha)) { flushLista(); (bufferTabela = bufferTabela || []).push(linha); continue; }
    flushTabela();
    const li = linha.match(/^\s*-\s+(.*)$/);
    if (li) { (bufferLista = bufferLista || []).push(noInline(li[1])); continue; }
    flushLista();
    const h = linha.match(/^(#{1,4})\s+(.*)$/);
    if (h) { content.push({ text: textoInline(h[2]), style: `h${h[1].length}` }); continue; }
    if (linha.trim() === '') continue;
    content.push({ text: textoInline(linha), margin: [0, 0, 0, 6] });
  }
  if (bufferCodigo !== null) pushCodigo(bufferCodigo.join('\n'));
  flushLista();
  flushTabela();

  return {
    content,
    styles: {
      h1: { fontSize: 18, bold: true, margin: [0, 0, 0, 10] },
      h2: { fontSize: 15, bold: true, margin: [0, 12, 0, 6] },
      h3: { fontSize: 12.5, bold: true, margin: [0, 10, 0, 4] },
      h4: { fontSize: 11, bold: true, margin: [0, 8, 0, 4] },
      codigo: { fontSize: 8.5, color: '#1f2937' },
      inlineCode: { color: '#0f766e' },
      tabela: { fontSize: 8.5 },
    },
    defaultStyle: { fontSize: 10 },
    pageMargins: [40, 40, 40, 40],
  };
}
